import { useMemo } from "react";

import { some } from "lodash/fp";

import {
  canInviteRead,
  isReadInvited,
} from "@app/analytics/Calendar/UpcomingEvents/utils";
import useGetCalendarState from "@app/analytics/GettingStarted/hooks/useGetCalendarState";
import {
  ProgressItemEnum,
  ProgressItemState,
} from "@app/analytics/GettingStarted/types";
import { isProgressItemCompleted } from "@app/analytics/GettingStarted/utils";
import useEventGroups from "@app/analytics/hooks/calendar/useEventGroups";
import useAuth from "@app/common/auth/useAuth";

const useGetMeetingInviteState = (progress: number): ProgressItemState => {
  const { user } = useAuth();

  // Require a calendar to be connected for the invite state to be completed
  const calendarState = useGetCalendarState(progress);

  const isCompleted = isProgressItemCompleted(
    progress,
    ProgressItemEnum.MEETING
  );

  const isInviteOnly = user?.calendarPermissionLevel === "invite_only";

  const { events } = useEventGroups({
    enabled: !isCompleted && isInviteOnly,
  });

  const hasInvitedRead = useMemo(() => {
    return some(
      (event) => canInviteRead(event) && isReadInvited(event),
      events
    );
  }, [events]);

  const hasConnectedCalendarProviders = calendarState.state !== "todo";

  const isUnmarkedComplete =
    !isCompleted &&
    hasConnectedCalendarProviders &&
    (!isInviteOnly || hasInvitedRead);

  const state = isUnmarkedComplete
    ? "unmarked_complete"
    : isCompleted
      ? "complete"
      : "todo";

  return useMemo(
    () => ({
      type: ProgressItemEnum.MEETING,
      state,
    }),
    [state]
  );
};

export default useGetMeetingInviteState;
